// Loads Stripe.js from the official CDN once per page and returns the
// initialised Stripe instance bound to our publishable key. Using the CDN
// script tag (rather than the @stripe/stripe-js npm shim) avoids adding a
// runtime dep and keeps the JS bundle smaller — Stripe Elements is rare on
// this app so a lazy CDN load on the first /learn unlock is fine.

declare global {
  interface Window {
    Stripe?: (publishableKey: string, opts?: Record<string, unknown>) => StripeJsInstance;
  }
}

export type StripeJsInstance = {
  elements: (opts: { clientSecret: string; appearance?: Record<string, unknown> }) => StripeElements;
  confirmPayment: (opts: {
    elements: StripeElements;
    confirmParams: { return_url: string };
    redirect?: 'always' | 'if_required';
  }) => Promise<{ error?: { message?: string; type?: string }; paymentIntent?: { id: string; status: string } }>;
};

export type StripeElements = {
  create: (type: string, opts?: Record<string, unknown>) => StripeElement;
  getElement: (type: string) => StripeElement | null;
};

export type StripeElement = {
  mount: (selector: string | HTMLElement) => void;
  unmount: () => void;
  on: (event: string, handler: (e: any) => void) => void;
  destroy: () => void;
};

let cachedPromise: Promise<StripeJsInstance | null> | null = null;

export const useStripeJs = () => {
  const config = useRuntimeConfig();
  const publishableKey = (config.public as { stripePublishableKey?: string }).stripePublishableKey;

  const loadStripe = async (): Promise<StripeJsInstance | null> => {
    if (typeof window === 'undefined') return null;
    if (!publishableKey) {
      console.error('[useStripeJs] NUXT_PUBLIC_STRIPE_PUBLISHABLE_KEY is not set');
      return null;
    }
    if (cachedPromise) return cachedPromise;

    cachedPromise = new Promise((resolve, reject) => {
      // If another caller already injected the tag (or we hot-reloaded), reuse.
      if (window.Stripe) {
        resolve(window.Stripe(publishableKey));
        return;
      }
      const existing = document.querySelector<HTMLScriptElement>('script[src^="https://js.stripe.com/v3"]');
      const onLoad = () => {
        if (window.Stripe) {
          resolve(window.Stripe(publishableKey));
        } else {
          reject(new Error('Stripe.js loaded but window.Stripe is unavailable'));
        }
      };
      if (existing) {
        existing.addEventListener('load', onLoad, { once: true });
        existing.addEventListener('error', () => reject(new Error('Stripe.js failed to load')), { once: true });
        return;
      }
      const script = document.createElement('script');
      script.src = 'https://js.stripe.com/v3/';
      script.async = true;
      script.addEventListener('load', onLoad, { once: true });
      script.addEventListener('error', () => reject(new Error('Stripe.js failed to load')), { once: true });
      document.head.appendChild(script);
    });

    return cachedPromise;
  };

  return { loadStripe };
};
